import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import StudioShell from "@/components/StudioShell";
import { ChevronLeft, ChevronRight, Download, Maximize2 } from "lucide-react";
import { toast } from "sonner";
import { Slide } from "@/components/SlideRenderers";
import { LogoOverlay } from "@/pages/BrandSettings";


export default function PresentationViewer() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [i, setI] = useState(0);
  const [exporting, setExporting] = useState(null);
  const [brand, setBrand] = useState(null);

  useEffect(() => {
    api.get(`/projects/${id}`).then(r => setProject(r.data)).catch(e => toast.error(apiError(e, "Could not load project")));
    api.get("/brand-settings").then(r => setBrand(r.data)).catch(e => console.warn("brand settings unavailable", e));
  }, [id]);

  const slideCount = project?.presentation?.slides?.length || 1;
  useEffect(() => {
    const h = (e) => { if(e.key==="ArrowRight") setI(x=>Math.min(x+1, slideCount-1)); if(e.key==="ArrowLeft") setI(x=>Math.max(x-1,0)); };
    window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h);
  }, [slideCount]);

  if (!project) return <StudioShell><div className="p-12">Loading…</div></StudioShell>;
  const slides = project.presentation?.slides || [];
  if (slides.length === 0) return <StudioShell projectContext={project}><div className="p-12 text-sm text-stone-500">No presentation yet. Generate one from the mood boards.</div></StudioShell>;

  const slide = slides[i];

  const doExport = async (fmt) => {
    setExporting(fmt);
    try {
      const r = await api.get(`/projects/${id}/export/${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${project.name.replace(/[^a-z0-9-_ ]/gi, "").trim().replace(/\s+/g, "_") || "presentation"}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch { toast.error(`${fmt.toUpperCase()} export failed`); }
    setExporting(null);
  };

  return (
    <StudioShell projectContext={project}>
      <div className="px-6 sm:px-12 py-8 max-w-[1500px] mx-auto">
        <div className="flex flex-wrap items-center justify-between mb-6 gap-3">
          <div>
            <div className="eyebrow mb-1">Client Presentation · 16:9</div>
            <div className="font-serif-editorial text-2xl">{project.name}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={()=>doExport("pdf")} disabled={!!exporting} data-testid="present-export-pdf" className="inline-flex items-center gap-2 px-4 py-2 border border-stone-300 text-sm disabled:opacity-50"><Download size={14} className={exporting==="pdf"?"animate-bounce":""}/> {exporting==="pdf"?"Building PDF…":"Export PDF"}</button>
            <button onClick={()=>doExport("pptx")} disabled={!!exporting} data-testid="present-export-pptx" className="inline-flex items-center gap-2 px-4 py-2 border border-stone-300 text-sm disabled:opacity-50"><Download size={14} className={exporting==="pptx"?"animate-bounce":""}/> {exporting==="pptx"?"Building PPTX…":"Export PPTX"}</button>
            <button onClick={()=>document.documentElement.requestFullscreen?.()} className="inline-flex items-center gap-2 px-4 py-2 border border-stone-300 text-sm" data-testid="present-fullscreen"><Maximize2 size={14}/></button>
          </div>
        </div>

        <div className="slide-shadow bg-white border relative" style={{ borderColor: "#DCDCD5" }} data-testid={`slide-${i}`}>
          <Slide slide={slide} project={project} />
          <LogoOverlay brand={brand} />
        </div>

        <div className="flex items-center justify-between mt-6">
          <button onClick={()=>setI(Math.max(0,i-1))} disabled={i===0} data-testid="slide-prev" className="inline-flex items-center gap-2 px-4 py-2 border border-stone-300 text-sm disabled:opacity-40"><ChevronLeft size={14}/> Prev</button>
          <div className="font-mono-lab text-xs text-stone-500">{String(i+1).padStart(2,'0')} / {String(slides.length).padStart(2,'0')}</div>
          <button onClick={()=>setI(Math.min(slides.length-1,i+1))} disabled={i===slides.length-1} data-testid="slide-next" className="inline-flex items-center gap-2 px-4 py-2 border border-stone-300 text-sm disabled:opacity-40">Next <ChevronRight size={14}/></button>
        </div>

        <div className="mt-8 grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
          {slides.map((s,idx)=>(
            <button key={`${s.type}-${s.title}-${idx}`} onClick={()=>setI(idx)} data-testid={`slide-thumb-${idx}`} className={`aspect-[16/9] text-[10px] font-mono-lab p-2 text-left overflow-hidden border ${idx===i?"border-black":"border-stone-200 hover:border-stone-400"}`}>
              <div className="opacity-60">0{idx+1}</div>
              <div className="mt-1 line-clamp-2">{s.title}</div>
            </button>
          ))}
        </div>
      </div>
    </StudioShell>
  );
}
